/**
 * DocumentSeal - マイページ「御見積書の印影」セクション
 *
 * 自分が発行する御見積書 PDF に載せるロゴ・社印・サインを登録する。
 * 画像は purpose=user_asset で S3 へ上げ、S3 キーを PATCH /api/me で保存する
 * （キーは AuthUser.document_*_key。PDF 生成時にサーバーが URL へ解決する）。
 *
 * 会社名・住所・肩書きは別画面で入力済みのものを流用するため、ここでは扱わない:
 *   会社名 = 所属（プロフィール） / 住所 = 請求先住所（個人情報） /
 *   肩書き = タイトル（プロフィール）
 * 現在の値は参考として読み取り専用で並べ、未入力なら該当画面へ促す。
 *
 * 社印だけはサイズ（mm 角）を指定できる。実物の角印はおおむね 18〜24mm。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Button from "../../../components/button/Button";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../../components/block/TitleBlock";
import SvgLoadingAnimation from "../../../components/loading/index";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import type { StatusType } from "../../../components/status-indicator/StatusIndicator";
// @ts-ignore - JS モジュール（型定義なし）。複数行に折ると ts-ignore が効かないため 1 行で書く。
import { useGetCurrentUserQuery, useUpdateCurrentUserMutation } from "../../../app/accounts/authuser";
import { useInitiateUploadMutation } from "../../../app/services/uploadApi";
import { uploadFileDirect } from "../../../utils/s3DirectUpload";

/** 社印サイズ（mm 角）の指定範囲。既存の御見積書は 25mm 角で刷っている。 */
const SEAL_SIZE_MIN_MM = 10;
const SEAL_SIZE_MAX_MM = 35;
const SEAL_SIZE_DEFAULT_MM = 25;

/** 印影に使える画像の上限（PDF に貼るだけなので小さめに抑える）。 */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const SAVED_DISPLAY_MS = 1000;
const ERROR_DISPLAY_MS = 3000;

type SealField =
  | "document_logo_key"
  | "document_seal_key"
  | "document_signature_key";

type SealUrlField =
  | "document_logo_url"
  | "document_seal_url"
  | "document_signature_url";

interface SealSlot {
  field: SealField;
  /** API が返す解決済み URL のフィールド（読み取り専用）。 */
  urlField: SealUrlField;
  label: string;
  description: string;
  /** プレビュー枠の形。社印だけ正方形で見せる。 */
  square?: boolean;
}

const SLOTS: SealSlot[] = [
  {
    field: "document_logo_key",
    urlField: "document_logo_url",
    label: "ロゴ",
    description:
      "用紙の右上（既定の SOREMO ロゴの位置）に入ります。横長の画像が収まりよく出ます。",
  },
  {
    field: "document_seal_key",
    urlField: "document_seal_url",
    label: "社印",
    description:
      "発行者欄の下に右寄せで入ります。背景が透過した PNG がきれいです。",
    square: true,
  },
  {
    field: "document_signature_key",
    urlField: "document_signature_url",
    label: "サイン",
    description: "氏名の下に入ります。背景が透過した PNG がきれいです。",
  },
];

const PreviewBox = styled.div<{ $square?: boolean }>`
  position: relative;
  width: ${({ $square }) => ($square ? "112px" : "100%")};
  max-width: 240px;
  height: 112px;
  border: 1px dashed var(--soremo-border);
  border-radius: 6px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const UploadLabel = styled.label.attrs({ className: "bodytext-11" })`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 100%;
  color: var(--soremo-gray);
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 22px;
    color: var(--soremo-light-gray);
  }

  &:hover {
    color: var(--soremo-blue);

    .material-symbols-rounded {
      color: var(--soremo-blue);
    }
  }

  input {
    display: none;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 14px;
  }
`;

const SlotRow = styled.div.attrs({ className: "u-row u-gap16" })`
  align-items: flex-start;
  flex-wrap: wrap;
`;

const DocumentSeal: React.FC = () => {
  const { data: userData, isLoading } = useGetCurrentUserQuery();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const [initiateUpload] = useInitiateUploadMutation();

  const [status, setStatus] = useState<StatusType>("idle");
  const [uploadingField, setUploadingField] = useState<SealField | null>(null);
  const [sealSize, setSealSize] = useState<number>(SEAL_SIZE_DEFAULT_MM);
  /**
   * アップロード直後・削除直後のプレビュー URL。
   * `/api/me` の再取得を待たずに見た目を確定させるための上書き
   * （空文字 = 削除済み、undefined = サーバーの値をそのまま使う）。
   */
  const [previewUrls, setPreviewUrls] = useState<Partial<Record<SealField, string>>>(
    {},
  );
  const sizeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const me = userData?.data;

  useEffect(() => {
    if (me?.document_seal_size_mm) setSealSize(me.document_seal_size_mm);
  }, [me?.document_seal_size_mm]);

  useEffect(
    () => () => {
      if (sizeDebounceRef.current) clearTimeout(sizeDebounceRef.current);
    },
    [],
  );

  const save = useCallback(
    async (patch: Record<string, string | number>) => {
      setStatus("saving");
      try {
        await updateCurrentUser(patch).unwrap();
        setStatus("saved");
        setTimeout(() => setStatus("idle"), SAVED_DISPLAY_MS);
        return true;
      } catch {
        setStatus("error");
        setTimeout(() => setStatus("idle"), ERROR_DISPLAY_MS);
        return false;
      }
    },
    [updateCurrentUser],
  );

  const handleSelect = useCallback(
    async (field: SealField, input: HTMLInputElement) => {
      const file = input.files?.[0];
      // 同じファイルを選び直しても change が発火するよう、毎回クリアしておく
      input.value = "";
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error("画像ファイルを指定してください");
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error("画像は 5MB 未満にしてください");
        return;
      }
      setUploadingField(field);
      try {
        const uploaded = await uploadFileDirect(initiateUpload, file, {
          purpose: "user_asset",
          tooLargeMessage: "アップロードできる画像は 50MB 未満のみです",
        });
        setPreviewUrls((prev) => ({ ...prev, [field]: uploaded.url }));
        await save({ [field]: uploaded.fileKey });
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "画像のアップロードに失敗しました",
        );
      } finally {
        setUploadingField(null);
      }
    },
    [initiateUpload, save],
  );

  const handleRemove = useCallback(
    (field: SealField) => {
      setPreviewUrls((prev) => ({ ...prev, [field]: "" }));
      save({ [field]: "" });
    },
    [save],
  );

  const handleSealSizeChange = useCallback(
    (value: number) => {
      setSealSize(value);
      if (sizeDebounceRef.current) clearTimeout(sizeDebounceRef.current);
      sizeDebounceRef.current = setTimeout(() => {
        save({ document_seal_size_mm: value });
      }, 500);
    },
    [save],
  );

  if (isLoading) return <SvgLoadingAnimation />;

  // 表示 URL はサーバーが S3 キーから解決して返す（document_*_url）。
  // 直後の見た目だけローカルの上書きを優先する。
  const urlFor = (slot: SealSlot): string => {
    const preview = previewUrls[slot.field];
    if (preview !== undefined) return preview;
    return (me?.[slot.urlField] as string | undefined) ?? "";
  };

  const hasImage = (slot: SealSlot): boolean => Boolean(urlFor(slot));

  return (
    <div className="u-col u-gap24 u-w100">
      <TitleBlock
        title="御見積書の印影"
        description="ステップ見積もり -超松竹梅- で書き出す御見積書 PDF の発行者欄に、ロゴ・社印・サインを載せられます。"
      />

      <div className="u-col u-gap8">
        <span className="bodytext-11" style={{ color: "var(--soremo-gray)" }}>
          発行者欄に出る文字情報（別の画面で設定します）
        </span>
        <ul className="u-col u-gap4 bodytext-13">
          <li>会社名: {me?.affiliation_jp || "（未入力：プロフィールの「所属」）"}</li>
          <li>肩書き: {me?.title_jp || "（未入力：プロフィールの「タイトル」）"}</li>
          <li>
            住所:{" "}
            {me?.billing_address_line1
              ? "個人情報の「請求先住所」を使用します"
              : "（未入力：個人情報の「請求先住所」）"}
          </li>
        </ul>
      </div>

      {SLOTS.map((slot) => (
        <div key={slot.field} className="u-col u-gap8">
          <label>{slot.label}</label>
          <SlotRow>
            <PreviewBox $square={slot.square}>
              {hasImage(slot) ? (
                <>
                  <img src={urlFor(slot)} alt={slot.label} />
                  <RemoveButton
                    type="button"
                    aria-label={`${slot.label}を削除`}
                    onClick={() => handleRemove(slot.field)}
                  >
                    <span className="material-symbols-rounded">close</span>
                  </RemoveButton>
                </>
              ) : (
                <UploadLabel>
                  <span className="material-symbols-rounded">
                    {uploadingField === slot.field
                      ? "hourglass_top"
                      : "add_photo_alternate"}
                  </span>
                  {uploadingField === slot.field
                    ? "アップロード中…"
                    : `${slot.label}を選択`}
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingField !== null}
                    onChange={(e) => handleSelect(slot.field, e.target)}
                  />
                </UploadLabel>
              )}
            </PreviewBox>
            <p
              className="bodytext-11"
              style={{ color: "var(--soremo-gray)", flex: "1 1 200px" }}
            >
              {slot.description}
            </p>
          </SlotRow>

          {slot.field === "document_seal_key" && hasImage(slot) && (
            <div className="u-row u-gap8 u-align-center">
              <span className="bodytext-13">印字サイズ</span>
              <input
                type="range"
                min={SEAL_SIZE_MIN_MM}
                max={SEAL_SIZE_MAX_MM}
                step={1}
                value={sealSize}
                aria-label="社印の印字サイズ（mm）"
                onChange={(e) => handleSealSizeChange(Number(e.target.value))}
                style={{ maxWidth: 240 }}
              />
              <span className="bodytext-13">{sealSize}mm 角</span>
            </div>
          )}
        </div>
      ))}

      <div className="u-row u-gap8">
        <Button
          size="small"
          theme="secondary"
          variant="outlined"
          width="fit"
          leftIcon="open_in_new"
          onClick={() => window.open("/tool/step-estimate", "_blank")}
        >
          ステップ見積もりを開く
        </Button>
      </div>

      <StatusIndicator status={status} />
    </div>
  );
};

export default DocumentSeal;
